import i18n from "@sg/i18n";
import { agent as baseAgent } from "@sg/query";
import { notify } from "@sg/ui";
import adminPrefStore from "~/store/adminPref";
import authStore from "~/store/auth";

const API_PREFIX = `${import.meta.env.VITE_API_PREFIX || ""}/api`;

type Labels = string[];

// Define your custom options interface
interface CustomOptions {
  labels?: Labels;
  okLabels?: Labels;
  errLabels?: Labels;
}

// Augment the ky module to include the custom options
declare module "ky" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface Options extends CustomOptions {}
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface NormalizedOptions extends CustomOptions {}
}

function getLabel(message: string, labels?: Labels, specificLabels?: Labels) {
  const resolved = specificLabels || labels;

  if (!resolved || resolved.length === 0) return "";

  if (resolved.length === 1) return resolved[0];

  return i18n.t(message, {
    resource: i18n.t(`resource:${resolved[1]}`),
    verb: i18n.t(`operation:${resolved[0]}`),
  });
}

// Submit a notification if a get error fails.
// Other method errors are handled on a case by case basis.
async function notifyGetError(response: Response, description?: string) {
  const { statusText, status } = response;
  const errorData = await response.json();
  const errorDetail = errorData?.detail || [];

  const message = Array.isArray(errorDetail)
    ? errorDetail?.map((detail: { msg: string }) => detail.msg)?.join(". ")
    : errorDetail;

  const messages = [description, message].filter((f) => f).join("\n");

  notify.alert(messages, `[${status}] - ${statusText}`);
}
const agent = baseAgent.extend({
  prefixUrl: `${API_PREFIX}/v1`,
  retry: 0,
  hooks: {
    beforeRequest: [
      (request) => {
        const { authToken } = authStore.getState();
        const { orgIdOverride } = adminPrefStore.getState();
        request.headers.set("Authorization", `Bearer ${authToken}`);

        // TODO only set org override if global admin user.
        if (orgIdOverride) {
          request.headers.set("X-Admin-Org", orgIdOverride);
        }
      },
    ],
    afterResponse: [
      async (_, options, response) => {
        const labels = options.labels || options.okLabels || [];
        if (response.status === 401 && !window.location.href.endsWith("login")) {
          window.location.href = "/login";
        }
        if (labels && labels.length > 0 && response.ok) {
          notify.success(getLabel("result_success", options.labels, options.okLabels));
        }
      },
    ],
    beforeError: [
      async (error) => {
        const { options, response } = error;
        await notifyGetError(response, getLabel("result_failure", options.labels, options.errLabels));

        return error;
      },
    ],
  },
});

export { agent };
